import React from 'react';
import { Box, Button, Chip, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EncycGamesLogo from '@components/layout/EncycGamesLogo';
import { brandColors } from '@config/brandTokens';

export const authCardSx = {
  width: '100%',
  overflow: 'hidden',
  bgcolor: brandColors.white,
  borderRadius: 3,
  border: `1px solid ${brandColors.navy}14`,
  boxShadow: '0 8px 28px rgba(17,23,57,0.08)',
} as const;

export const authTitleSx = {
  color: brandColors.white,
  fontFamily: '"Sora", Arial, sans-serif',
  fontWeight: 800,
  lineHeight: 1.25,
} as const;

export const authSubtitleSx = {
  color: 'rgba(255,255,255,0.82)',
  mt: 0.5,
  lineHeight: 1.45,
} as const;

export const authPrimaryCtaSx = {
  textTransform: 'none' as const,
  fontWeight: 800,
  bgcolor: brandColors.orange,
  color: brandColors.white,
  '&:hover': { bgcolor: '#f07a3d' },
};

export const authLinkSx = {
  '& a': {
    color: brandColors.blue,
    textDecorationColor: `${brandColors.blue}66`,
  },
};

const chromeLinkSx = {
  textTransform: 'none' as const,
  minWidth: 0,
  px: 1,
  fontWeight: 700,
};

interface AuthFormCardProps {
  title: string;
  subtitle?: string;
  roleLabel?: string;
  children: React.ReactNode;
  /**
   * Compact card chrome: Cancel → home, logo top-right.
   * Login uses this without Back. Register / verify pages also pass `backTo`.
   */
  chrome?: boolean;
  /** Same as player register: typically sign-in (`/login`), preserving returnUrl. */
  backTo?: string;
}

/** Branded EncycGames card for login / register forms. */
const AuthFormCard: React.FC<AuthFormCardProps> = ({
  title,
  subtitle,
  roleLabel,
  children,
  chrome = false,
  backTo,
}) => {
  const showChrome = chrome || Boolean(backTo);

  return (
    <Paper elevation={0} sx={authCardSx}>
      {showChrome ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: { xs: 1.5, md: 2 },
            py: 0.75,
            bgcolor: brandColors.white,
            borderBottom: `1px solid ${brandColors.navy}12`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, ...authLinkSx }}>
            {backTo ? (
              <Button
                component={RouterLink}
                to={backTo}
                size="small"
                sx={{ ...chromeLinkSx, color: brandColors.blue }}
              >
                ← Back
              </Button>
            ) : null}
            <Button
              component={RouterLink}
              to="/"
              size="small"
              sx={{ ...chromeLinkSx, color: brandColors.slate }}
            >
              Cancel
            </Button>
          </Box>
          <EncycGamesLogo width={132} />
        </Box>
      ) : null}
      <Box
        sx={{
          px: { xs: 3, md: 4 },
          py: { xs: 2.25, md: 2.75 },
          background: `linear-gradient(135deg, ${brandColors.navy} 0%, #1a2560 58%, ${brandColors.blue} 100%)`,
          color: brandColors.white,
        }}
      >
        {roleLabel ? (
          <Chip
            label={roleLabel}
            size="small"
            sx={{
              mb: 1,
              height: 22,
              fontWeight: 800,
              fontSize: '0.7rem',
              bgcolor: 'rgba(255,255,255,0.14)',
              color: brandColors.white,
              border: '1px solid rgba(255,255,255,0.28)',
            }}
          />
        ) : null}
        <Typography component="h1" variant="h5" sx={authTitleSx}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={authSubtitleSx}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 3.5 } }}>{children}</Box>
    </Paper>
  );
};

export default AuthFormCard;
